import logger from '../../utils/logger';
import React, { useCallback, memo, useState } from 'react';
import { Home, Database, PlusCircle, List, BarChart3, Calendar, Settings, MessageCircle, Terminal, FileText, ChevronRight, ChevronLeft, Globe, Server, Send, MessageSquare, Files, Workflow, Zap, Activity, Monitor, Shield, AlertTriangle, Bug, TrendingUp, CreditCard, Crown, FolderOpen, Variable, Bot, Boxes } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { isAdmin } from '../../utils/adminUtils';
import { calculateTrialDaysRemaining } from '../../services/subscriptionService';
import { isOnPrem } from '../../utils/featureFlags';

const Sidebar = ({ onNavigate, currentPage, isExpandedOverride, onToggleExpand }) => {
  const expanded = isExpandedOverride;
  const { user } = useAuth();
  const [tooltip, setTooltip] = useState({ visible: false, label: '', color: '', top: 0 });
  const userIsAdmin = isAdmin(user);

  // Calculate trial info for badge (hidden in on-prem mode)
  const isTrialUser = !isOnPrem() && (user?.isTrialUser || false);
  const trialDaysRemaining = isTrialUser && user?.planExpiresAt
    ? calculateTrialDaysRemaining(user.planExpiresAt)
    : 0;

  // Check if AI features are enabled
  const aiEnabled = user?.aiConsentGiven || false;

  const navItems = [
    { id: 'home', label: 'Home', icon: Home, color: '#3b82f6' },
    { id: 'connections', label: 'Connections', icon: Database, color: '#10b981' },
    { id: 'data-models', label: 'Data Models', icon: Boxes, color: '#6366f1' },
    { id: 'sql-operations', label: 'SQL Operations', icon: FileText, color: '#14b8a6' },
    { id: 'file-explorer', label: 'File Explorer', icon: FolderOpen, color: '#06b6d4' },
    { id: 'global-variables', label: 'Global Variables', icon: Variable, color: '#8b5cf6' },
    { id: 'create-pipeline', label: 'Create Pipeline', icon: PlusCircle, color: '#f59e0b' },
    { id: 'kafka', label: 'Kafka Messaging', icon: Send, color: '#8b5cf6' },
    { id: 'monitor-pipelines', label: 'Monitor Pipelines', icon: BarChart3, color: '#06b6d4' },
    { id: 'schedule-pipelines', label: 'Schedule Pipelines', icon: Calendar, color: '#f97316' },
    { id: 'pipeline-sequences', label: 'Pipeline Sequences', icon: List, color: '#a855f7' },
    // AI features - only show if AI is enabled
    // CodeGen Jobs moved to floating Thinking Prompt panel
    ...(aiEnabled ? [
      { id: 'agents', label: 'Agents', icon: Bot, color: '#f59e0b' },
      { id: 'data-quality', label: 'Data Quality', icon: AlertTriangle, color: '#dc2626' },
      { id: 'debugging', label: 'AI Debugging', icon: Bug, color: '#9333ea' },
      { id: 'optimization', label: 'AI Optimization', icon: TrendingUp, color: '#059669' },
    ] : []),
    { id: 'admin-dashboard', label: 'Admin Dashboard', icon: Shield, color: '#ef4444', adminOnly: true },
    ...(isOnPrem() ? [
      { id: 'config-promotion', label: 'Config Promotion', icon: Send, color: '#8b5cf6' },
    ] : []),
  ];

  const handleNavigate = useCallback((pageId) => {
    if (typeof onNavigate === 'function') {
      onNavigate(pageId);
    }
  }, [onNavigate]);

  const NavButton = memo(({ item, isActive }) => {
    const Icon = item.icon;
    const isGreyedOut = item.greyedOut;

    const handleMouseEnter = (e) => {
      if (!expanded) {
        const rect = e.currentTarget.getBoundingClientRect();
        setTooltip({
          visible: true,
          label: item.label,
          color: item.color,
          top: rect.top + rect.height / 2,
        });
      }
    };

    const handleMouseLeave = () => {
      setTooltip({ visible: false, label: '', color: '', top: 0 });
    };

    return (
      <button
        onClick={() => handleNavigate(item.id)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="relative flex items-center h-10 w-full group transition-all duration-200 rounded-lg"
        style={{
          background: isActive && !isGreyedOut ? `linear-gradient(135deg, ${item.color}, ${item.color}dd)` : 'transparent',
          boxShadow: isActive && !isGreyedOut ? `0 2px 12px ${item.color}50, inset 0 1px 0 rgba(255,255,255,0.2)` : 'none',
          opacity: isGreyedOut ? 0.5 : 1,
          transform: 'translateZ(0)',
        }}
      >
        {/* Background Glow on Hover */}
        {!isActive && !isGreyedOut && (
          <div
            className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150"
            style={{
              background: `${item.color}1A`,
              transform: 'translateZ(0)',
            }}
          />
        )}

        <div className={`flex items-center justify-center flex-shrink-0 relative z-10 ${expanded ? 'w-16' : 'w-full'}`}>
          <Icon
            size={22}
            className={`transition-colors duration-150 ${
              isGreyedOut
                ? 'text-gray-400'
                : isActive
                  ? 'text-white'
                  : 'text-gray-800'
            }`}
          />
          {/* Trial Badge (Collapsed State) */}
          {item.showBadge && !expanded && (
            <div className={`absolute -top-1 -right-1 ${item.badgeColor} text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-lg animate-pulse`}>
              {trialDaysRemaining}
            </div>
          )}
        </div>
        {expanded && (
          <div className="flex items-center justify-between flex-1">
            <span className={`truncate relative z-10 transition-colors duration-150 ${
              isGreyedOut
                ? 'text-gray-400'
                : isActive
                  ? 'font-semibold text-white'
                  : 'font-medium text-gray-800'
            }`}>
              {item.label}
            </span>
            {/* Trial Badge (Expanded State) */}
            {item.showBadge && (
              <div className={`ml-2 ${item.badgeColor} text-white text-xs font-bold px-2 py-0.5 rounded-full shadow-md animate-pulse`}>
                {item.badgeText}
              </div>
            )}
          </div>
        )}
      </button>
    );
  });

  return (
  <>
    <div
      className={`sidebar h-full
        will-change-[width] transform-gpu transition-[width] duration-200 ease-out
        ${expanded ? 'w-64' : 'w-16'} overflow-hidden
        bg-white/50 dark:bg-gray-900/50 backdrop-blur-2xl border-r border-white/30 dark:border-white/[0.06] shadow-xl`}
      role="navigation"
      aria-expanded={expanded}
      style={{
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
        boxShadow: 'inset -1px 0 0 0 rgba(255,255,255,0.15), 4px 0 24px -4px rgba(15, 23, 42, 0.08)',
      }}
    >
      {/* Navigation */}
      <nav className="flex flex-col h-full pt-4 min-h-0">
        {/* Toggle Button as first nav item */}
        <div className="px-3 mb-4 flex-shrink-0">
          <button
            onClick={(e) => {
              logger.debug('Toggle button clicked!', expanded);
              onToggleExpand();
            }}
            className="w-full h-10 flex items-center justify-center rounded-lg bg-gray-100/80 dark:bg-white/[0.06] hover:bg-gray-200/80 dark:hover:bg-white/[0.1] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-all duration-200 border border-gray-200/60 dark:border-white/[0.06]"
            title={expanded ? "Collapse sidebar" : "Expand sidebar"}
          >
            {expanded ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
          </button>
        </div>

        <div className="flex-1 min-h-0 space-y-2 px-3 overflow-y-auto hide-scrollbar">
          {navItems.filter(item => !item.adminOnly || userIsAdmin).map(item => (
            <NavButton
              key={item.id}
              item={item}
              isActive={currentPage === item.id}
            />
          ))}
        </div>
        
        {/* Bottom section - API Debug (admins only) */}
        {/* Thinking Prompt moved to floating dock */}
        {userIsAdmin && (
          <div className="px-3 pb-3 space-y-2 flex-shrink-0">
            <NavButton
              item={{
                id: 'api-debug',
                label: 'API Debug',
                icon: Settings,
                color: '#9ca3af',
                greyedOut: true
              }}
              isActive={currentPage === 'api-debug'}
            />
          </div>
        )}
      </nav>
    </div>

    {/* Fixed Position Tooltip - rendered outside sidebar to avoid clipping */}
    {tooltip.visible && (
      <div
        className="fixed pointer-events-none"
        style={{
          left: '76px', // 64px sidebar + 12px gap
          top: tooltip.top,
          transform: 'translateY(-50%)',
          zIndex: 9999,
        }}
      >
        <div
          className="flex items-center px-3 py-2 rounded-lg shadow-lg"
          style={{
            background: tooltip.color,
            boxShadow: `0 4px 20px ${tooltip.color}60`,
          }}
        >
          {/* Arrow */}
          <div
            className="absolute -left-1.5 top-1/2 -translate-y-1/2"
            style={{
              width: 0,
              height: 0,
              borderTop: '6px solid transparent',
              borderBottom: '6px solid transparent',
              borderRight: `6px solid ${tooltip.color}`,
            }}
          />
          <span className="text-white font-medium text-sm whitespace-nowrap">
            {tooltip.label}
          </span>
        </div>
      </div>
    )}
  </>
  );
};

export default memo(Sidebar);